import React from 'react';
import { Click, ExtendCSS, Flex, IconButton, Logo, Text, View } from 'vcc-ui';
import { topbar } from '../docs.config';

export interface TopbarProps {
  onMobileNavClick?(): void;
  mobileNavIsOpen?: boolean;
}

export const Topbar: React.FC<TopbarProps> = ({
  onMobileNavClick,
  mobileNavIsOpen = false,
}) => {
  return (
    <View justifyContent="center" extend={topbarCSS({ mobileNavIsOpen })}>
      <View grow={1} extend={topbarContentCSS}>
        <Flex extend={logoWrapperCSS}>
          <Logo type="spreadmark" height={8} />
        </Flex>
        {onMobileNavClick ? (
          <MobileNavButton
            onClick={onMobileNavClick}
            isOpen={mobileNavIsOpen}
          />
        ) : null}
        <View extend={navItemsWrapperCSS({ mobileNavIsOpen })}>
          {topbar.map(({ href, text }, i) => (
            <NavItem key={i} href={href}>
              {text}
            </NavItem>
          ))}
        </View>
      </View>
    </View>
  );
};

const NavItem: React.FC<{
  href: string;
  children: string;
  isActive?: boolean;
}> = ({ href, children, isActive }) => (
  <Click href={href} extend={navItemCSS(!!isActive)}>
    <Text subStyle="emphasis">{children}</Text>
  </Click>
);

const MobileNavButton: React.FC<{ onClick: () => void; isOpen: boolean }> = ({
  onClick,
  isOpen,
}) => (
  <View extend={mobileNavButtonCSS}>
    <IconButton
      iconName={isOpen ? 'navigation-close' : 'navigation-menu'}
      onClick={onClick}
      aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
    />
  </View>
);

const mobileNavButtonCSS: ExtendCSS = {
  gridColumn: 2,
  justifyContent: 'center',
  fromL: {
    display: 'none',
  },
};

const topbarCSS =
  ({ mobileNavIsOpen }: { mobileNavIsOpen: boolean }): ExtendCSS =>
  ({ theme: { color } }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 'var(--topbar-z-index)',
    height: 'var(--topbar-height)',
    backgroundColor: color.background.secondary,
    extend: [
      {
        condition: mobileNavIsOpen,
        style: {
          untilL: {
            height: 'calc(var(--topbar-height) * 2)',
            backgroundColor: color.background.primary,
            borderBottom: `1px solid ${color.ornament.divider}`,
          },
        },
      },
    ],
  });

const topbarContentCSS: ExtendCSS = {
  display: 'grid',
  alignSelf: 'center',
  alignItems: 'stretch',
  gridTemplateColumns: 'var(--grid-columns)',
  gridTemplateRows: 'var(--topbar-height)',
  columnGap: 'var(--grid-column-gap)',
  justifyContent: 'space-between',
  width: 'var(--content-width)',
};

const logoWrapperCSS: ExtendCSS = {
  width: 'max-content',
  alignSelf: 'center',
  '> img': {
    height: 7,
    fromL: {
      height: 8,
    },
  },
};

const navItemsWrapperCSS =
  ({ mobileNavIsOpen }: { mobileNavIsOpen: boolean }): ExtendCSS =>
  ({ theme: { baselineGrid } }) => ({
    gridColumn: 3,
    flexDirection: 'row',
    flexGrow: 1,
    gap: baselineGrid * 4,
    untilL: {
      extend: [
        {
          condition: !mobileNavIsOpen,
          style: {
            display: 'none',
          },
        },
        {
          condition: mobileNavIsOpen,
          style: {
            gridColumn: '1 / 3',
            gridRow: '2',
          },
        },
      ],
    },
  });

const navItemCSS =
  (isActive: boolean): ExtendCSS =>
  ({ theme: { color } }) => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
    borderBottomColor: isActive ? color.ornament.highlight : 'transparent',
    letterSpacing: 0.3,
    ':hover': {
      borderBottomColor: color.ornament.highlight,
    },
  });
